// 数据管理工具 - 统一处理配置和数据的读取
import configData from '../data/config.json';
import yogaData from '../data/yogaData.json';
import ConfigService from './configService';

class DataManager {
  constructor() {
    this.config = configData;
    this.data = yogaData;
    this.cache = new Map();
    this.cacheTimeout = 5 * 60 * 1000; // 5分钟缓存
  }

  // 检查是否使用服务器数据
  _useServerData() {
    return ConfigService.useServerData();
  }

  // 获取缓存键
  _getCacheKey(key) {
    return `${this._useServerData() ? 'server' : 'local'}_${key}`;
  }

  // 设置缓存
  _setCache(key, data) {
    const cacheKey = this._getCacheKey(key);
    this.cache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
  }

  // 获取缓存
  _getFromCache(key) {
    const cacheKey = this._getCacheKey(key);
    const cached = this.cache.get(cacheKey);

    if (cached && (Date.now() - cached.timestamp) < this.cacheTimeout) {
      return cached.data;
    }

    if (cached) {
      this.cache.delete(cacheKey);
    }

    return null;
  }

  invalidateCache(key) {
    this.cache.delete(this._getCacheKey(key));
  }

  // 通用API请求方法
  async _fetchFromServer(endpoint) {
    const url = ConfigService.getApiUrl(endpoint);

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        timeout: 5000, // 5秒超时
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
        throw new Error(`无法连接到服务器 ${url} - 请确保Django服务器正在运行`);
      }
      throw error;
    }
  }

  // 通用数据获取方法
  async _getData(key, localDataGetter, serverEndpoint) {
    if (!this._useServerData()) {
      const localData = localDataGetter();
      return Array.isArray(localData) ? localData : [];
    }

    // 服务器模式：只从服务器获取数据，不降级到本地
    // 检查缓存
    const cached = this._getFromCache(key);
    if (cached) {
      return Array.isArray(cached) ? cached : [];
    }

    // 从服务器获取数据，失败时抛出错误而不是降级
    const data = await this._fetchFromServer(serverEndpoint);
    const processedData = Array.isArray(data) ? data : [];
    this._setCache(key, processedData);
    return processedData;
  }

  // 获取健身动作列表（异步版本），可选过滤参数 { body_part, equipment, search }
  async getExercises(filters = {}) {
    const params = new URLSearchParams();
    if (filters.body_part) params.set('body_part', filters.body_part);
    if (filters.equipment) params.set('equipment', filters.equipment);
    if (filters.search) params.set('search', filters.search);
    const query = params.toString();
    const endpoint = `/exercises/${query ? `?${query}` : ''}`;

    // 无过滤时使用缓存；带过滤条件直接请求服务器
    if (!query) {
      return await this._getData('exercises', () => [], endpoint);
    }
    if (!this._useServerData()) {
      return [];
    }
    const data = await this._fetchFromServer(endpoint);
    return Array.isArray(data) ? data : [];
  }

  // 获取健身动作筛选元数据（部位/器材 + 中文 + 数量）
  async getExerciseMeta() {
    if (!this._useServerData()) {
      return { body_parts: [], equipments: [] };
    }
    const cached = this._getFromCache('exercise_meta');
    if (cached) return cached;
    const data = await this._fetchFromServer('/exercises/meta/');
    this._setCache('exercise_meta', data);
    return data;
  }

  // 获取单个健身动作详情（含中英文步骤与GIF）
  async getExerciseDetail(id) {
    if (!this._useServerData()) {
      return null;
    }
    const cacheKey = `exercise_${id}`;
    const cached = this._getFromCache(cacheKey);
    if (cached) return cached;
    const data = await this._fetchFromServer(`/exercises/${id}/`);
    this._setCache(cacheKey, data);
    return data;
  }

  // 获取体式配置（异步版本）
  async getPoses() {
    const posesData = await this._getData(
      'poses',
      () => this.config.poses || {},
      '/poses/'
    );

    // 如果是服务器数据（数组格式），转换为按分类分组的对象
    if (Array.isArray(posesData)) {
      console.log('📊 原始体式数据（数组）:', posesData);
      console.log('📊 原始体式数据数量:', posesData.length);

      // 定义分类顺序
      const categoryOrder = ['拜日式', '站立体式', '坐立体式', '后弯体式', '结束体式'];
      const groupedPoses = {};

      // 初始化所有分类
      categoryOrder.forEach(category => {
        groupedPoses[category] = [];
      });

      // 按分类统计
      const categoryStats = {};
      posesData.forEach((pose, index) => {
        const category = pose.category || '其他';
        const poseName = pose.chinese_name || pose.name || '未知';
        const orderIndex = pose.order_index || 0;
        console.log(`📊 体式${index + 1}: ${poseName} -> 分类: ${category} (ID: ${pose.id}, 顺序: ${orderIndex})`);

        if (!categoryStats[category]) {
          categoryStats[category] = 0;
        }
        categoryStats[category]++;

        if (!groupedPoses[category]) {
          groupedPoses[category] = [];
        }
        groupedPoses[category].push(pose);
      });

      console.log('📊 分类统计:', categoryStats);

      // 按照数据库中的 order_index 排序每个分类中的体式
      const orderedGroupedPoses = {};
      categoryOrder.forEach(category => {
        if (groupedPoses[category] && groupedPoses[category].length > 0) {
          // 按 order_index 排序，如果没有则按名称排序
          const sortedPoses = groupedPoses[category].sort((a, b) => {
            const orderA = a.order_index || 0;
            const orderB = b.order_index || 0;

            if (orderA !== orderB) {
              return orderA - orderB;
            }

            // 如果 order_index 相同，按名称排序
            const nameA = a.chinese_name || a.name || '';
            const nameB = b.chinese_name || b.name || '';
            return nameA.localeCompare(nameB);
          });

          orderedGroupedPoses[category] = sortedPoses;
          console.log(`📊 ${category} 排序后:`, sortedPoses.map(p => `${p.chinese_name || p.name} (${p.order_index || 0})`));
        }
      });

      // 添加其他分类
      Object.keys(groupedPoses).forEach(category => {
        if (!categoryOrder.includes(category) && groupedPoses[category].length > 0) {
          // 其他分类也按 order_index 排序
          const sortedPoses = groupedPoses[category].sort((a, b) => {
            const orderA = a.order_index || 0;
            const orderB = b.order_index || 0;
            return orderA !== orderB ? orderA - orderB : (a.chinese_name || a.name || '').localeCompare(b.chinese_name || b.name || '');
          });
          orderedGroupedPoses[category] = sortedPoses;
        }
      });

      console.log('📊 体式数据已按分类分组:', orderedGroupedPoses);
      console.log('📊 分组后的分类列表:', Object.keys(orderedGroupedPoses));
      return orderedGroupedPoses;
    }

    // 如果已经是对象格式，直接返回
    return posesData;
  }

  // 注意：体式顺序现在由数据库中的 order_index 字段控制
  // 不再需要硬编码的排序逻辑

  // 获取老师信息（异步版本）
  async getTeachers() {
    return await this._getData(
      'teachers',
      () => this.config.teachers || [],
      '/teachers/'
    );
  }

  // 获取瑜伽哲学（异步版本）
  async getPracticePhilosophy() {
    return await this._getData(
      'philosophies',
      () => this.config.practicePhilosophy || [],
      '/philosophies/'
    );
  }

  // 同步版本（严格模式：服务器模式下返回空数据）
  getPosesSync() {
    if (this._useServerData()) {
      console.warn('⚠️ 服务器模式下不应使用同步方法，返回空数据');
      return {}; // 严格模式：不降级到本地数据
    }
    return this.config.poses || {};
  }

  getTeachersSync() {
    if (this._useServerData()) {
      console.warn('⚠️ 服务器模式下不应使用同步方法，返回空数据');
      return []; // 严格模式：不降级到本地数据
    }
    return this.config.teachers || [];
  }

  getPracticePhilosophySync() {
    if (this._useServerData()) {
      console.warn('⚠️ 服务器模式下不应使用同步方法，返回空数据');
      return []; // 严格模式：不降级到本地数据
    }
    return this.config.practicePhilosophy || [];
  }

  // 获取哲学反思数据
  getPhilosophyReflections() {
    return this.data.philosophyReflections || {};
  }

  async getPhilosophyReflectionsByName(philosophyName) {
    const encodedName = encodeURIComponent(philosophyName);
    return await this._fetchFromServer(`/philosophy-reflections/by_philosophy/?philosophy_name=${encodedName}`);
  }

  // 获取分类配置
  getCategories() {
    return this.config.categories;
  }

  // 获取故事分类
  getStoryCategories() {
    return this.config.categories.storyCategories;
  }

  // 获取洞察类型
  getInsightTypes() {
    return this.config.categories.insightTypes;
  }

  // 获取感受选项
  getFeelingOptions() {
    return this.config.categories.feelingOptions;
  }

  // 获取社区故事（异步版本）
  async getCommunityStories() {
    return await this._getData(
      'communityStories',
      () => this.data.communityStories || [],
      '/stories/'
    );
  }

  // 获取感悟洞察（异步版本）
  async getFeelingInsights() {
    return await this._getData(
      'feelingInsights',
      () => this.data.feelingInsights || [],
      '/insights/'
    );
  }

  // 同步版本（提供降级数据以避免应用崩溃）
  getCommunityStoriesSync() {
    if (this._useServerData()) {
      console.warn('⚠️ 服务器模式下使用同步方法作为降级，请确保服务器正在运行');
      // 返回本地数据作为降级，避免应用崩溃
      return this.data.communityStories || [];
    }
    return this.data.communityStories || [];
  }

  getFeelingInsightsSync() {
    if (this._useServerData()) {
      console.warn('⚠️ 服务器模式下使用同步方法作为降级，请确保服务器正在运行');
      // 返回本地数据作为降级，避免应用崩溃
      return this.data.feelingInsights || [];
    }
    return this.data.feelingInsights || [];
  }

  // 获取练习反思（异步版本）
  async getPracticeReflections() {
    return await this._getData(
      'practiceReflections',
      () => this.data.poseReflections || [],
      '/reflections/'
    );
  }

  // 获取体式反思（新名称，异步版本）
  async getPoseReflections() {
    return await this.getPracticeReflections();
  }

  // 同步版本（提供降级数据以避免应用崩溃）
  getPracticeReflectionsSync() {
    if (this._useServerData()) {
      console.warn('⚠️ 服务器模式下使用同步方法作为降级，请确保服务器正在运行');
      // 返回本地数据作为降级，避免应用崩溃
      return this.data.poseReflections || [];
    }
    return this.data.poseReflections || [];
  }

  getPoseReflectionsSync() {
    if (this._useServerData()) {
      console.warn('⚠️ 服务器模式下使用同步方法作为降级，请确保服务器正在运行');
      // 返回本地数据作为降级，避免应用崩溃
      return this.data.poseReflections || [];
    }
    return this.data.poseReflections || [];
  }

  // 根据分类筛选故事
  getStoriesByCategory(categoryId) {
    if (categoryId === 'all') {
      return this.getCommunityStories();
    }
    return this.getCommunityStories().filter(story => story.category === categoryId);
  }

  // 根据类型筛选洞察
  getInsightsByType(typeId) {
    if (typeId === 'all') {
      return this.getFeelingInsights();
    }
    return this.getFeelingInsights().filter(insight => insight.type === typeId);
  }

  // 获取精选故事
  getFeaturedStories() {
    return this.getCommunityStories().filter(story => story.featured);
  }

  // 获取验证的洞察
  getVerifiedInsights() {
    return this.getFeelingInsights().filter(insight => insight.verified);
  }

  // 根据用户筛选反思
  getReflectionsByUser(username) {
    return this.getPoseReflections().filter(reflection => reflection.user === username);
  }

  // 根据日期筛选反思
  getReflectionsByDate(date) {
    return this.getPoseReflections().filter(reflection => reflection.date === date);
  }

  // 根据感受筛选反思
  getReflectionsByFeeling(feeling) {
    return this.getPoseReflections().filter(reflection => reflection.feeling === feeling);
  }

  // 搜索故事
  searchStories(keyword) {
    const stories = this.getCommunityStories();
    return stories.filter(story => {
      const authorName = typeof story.author === 'object' ?
        (story.author.name || story.author.username || '') :
        (story.author || '');

      return story.title.includes(keyword) ||
        story.content.includes(keyword) ||
        authorName.includes(keyword) ||
        (Array.isArray(story.tags) && story.tags.some(tag => tag.includes(keyword)));
    });
  }

  // 搜索洞察
  searchInsights(keyword) {
    const insights = this.getFeelingInsights();
    return insights.filter(insight => {
      const authorName = typeof insight.author === 'object' ?
        (insight.author.name || insight.author.username || '') :
        (insight.author || '');

      return insight.title.includes(keyword) ||
        insight.description.includes(keyword) ||
        authorName.includes(keyword) ||
        (Array.isArray(insight.relatedPoses) && insight.relatedPoses.some(pose => pose.includes(keyword)));
    });
  }

  // 获取所有体式名称（扁平化）- 异步版本
  async getAllPoseNames() {
    const poses = await this.getPoses();
    const allPoses = [];
    Object.values(poses).forEach(poseGroup => {
      if (Array.isArray(poseGroup)) {
        // 处理新的对象格式
        poseGroup.forEach(pose => {
          if (typeof pose === 'object' && pose.chineseName) {
            allPoses.push(pose.chineseName);
          } else if (typeof pose === 'string') {
            allPoses.push(pose);
          }
        });
      }
    });
    return allPoses;
  }

  // 同步版本
  getAllPoseNamesSync() {
    if (this._useServerData()) {
      console.warn('⚠️ 服务器模式下不应使用同步方法');
      return [];
    }
    const poses = this.config.poses || {};
    const allPoses = [];
    Object.values(poses).forEach(poseGroup => {
      if (Array.isArray(poseGroup)) {
        poseGroup.forEach(pose => {
          if (typeof pose === 'object' && pose.chineseName) {
            allPoses.push(pose.chineseName);
          } else if (typeof pose === 'string') {
            allPoses.push(pose);
          }
        });
      }
    });
    return allPoses;
  }

  // 根据中文名称获取体式详情 - 异步版本
  async getPoseByName(chineseName) {
    const poses = await this.getPoses();
    for (const category of Object.values(poses)) {
      if (Array.isArray(category)) {
        const pose = category.find(p =>
          (typeof p === 'object' && p.chineseName === chineseName) ||
          (typeof p === 'string' && p === chineseName)
        );
        if (pose) return pose;
      }
    }
    return null;
  }

  // 获取体式分类 - 异步版本
  async getPoseCategories() {
    const poses = await this.getPoses();
    return Object.keys(poses);
  }

  // 根据分类获取体式 - 异步版本
  async getPosesByCategory(category) {
    const poses = await this.getPoses();
    return poses[category] || [];
  }

  // 同步版本
  getPoseByNameSync(chineseName) {
    if (this._useServerData()) {
      console.warn('⚠️ 服务器模式下不应使用同步方法');
      return null;
    }
    const poses = this.config.poses || {};
    for (const category of Object.values(poses)) {
      if (Array.isArray(category)) {
        const pose = category.find(p =>
          (typeof p === 'object' && p.chineseName === chineseName) ||
          (typeof p === 'string' && p === chineseName)
        );
        if (pose) return pose;
      }
    }
    return null;
  }

  getPoseCategoriesSync() {
    if (this._useServerData()) {
      console.warn('⚠️ 服务器模式下不应使用同步方法');
      return [];
    }
    return Object.keys(this.config.poses || {});
  }

  getPosesByCategorySync(category) {
    if (this._useServerData()) {
      console.warn('⚠️ 服务器模式下不应使用同步方法');
      return [];
    }
    return (this.config.poses || {})[category] || [];
  }

  // 根据体式名称获取相关洞察
  getInsightsByPose(poseName) {
    return this.getFeelingInsights().filter(insight =>
      Array.isArray(insight.relatedPoses) && insight.relatedPoses.includes(poseName)
    );
  }

  // 获取统计信息
  getStatistics() {
    return {
      totalStories: this.getCommunityStories().length,
      totalInsights: this.getFeelingInsights().length,
      totalReflections: this.getPracticeReflections().length,
      featuredStories: this.getFeaturedStories().length,
      verifiedInsights: this.getVerifiedInsights().length,
      totalPoses: this.getAllPoseNames().length,
      totalTeachers: this.getTeachers().length
    };
  }
}

// 创建单例实例
const dataManager = new DataManager();

export default dataManager;
